import { call } from '@rental/contracts/client'
import { format, formatDateTime, plural } from '@rental/i18n'
import { cn } from '@rental/ui'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { SearchX, Users } from 'lucide-react'
import { useId } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { EmptyState } from '../components/EmptyState'
import { PageHeader } from '../components/PageHeader'
import { ListRow, ResultCount, ResultList, ROW_LINK } from '../components/Results'
import { SearchField, SearchForm, useSearchFields } from '../components/SearchForm'
import { StatusChip } from '../components/StatusChip'
import { Guard, LoadState } from '../components/states'
import { useLocale } from '../i18n'
import { problemOf } from '../problems'

const route = getRouteApi('/customers')

/**
 * The customers, for whoever reads customer records (UI-2g): anyone else who types its address reads
 * the refusal under its title, and nothing is asked of the server for it.
 */
export function CustomersScreen() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  return (
    <Guard allowed={can(access, 'customers.read')} title={m.customers.title}>
      <CustomersList />
    </Guard>
  )
}

/** CUS-1: customer records, found by name, e-mail or phone. */
function CustomersList() {
  const { locale, messages: m } = useLocale()
  const search = route.useSearch()
  const navigate = route.useNavigate()
  const asked = { q: search.q ?? '' }
  // Back, or a link to another search: the field follows the address.
  const [form, setForm] = useSearchFields(asked)
  const page = search.page ?? 1
  const list = useQuery({
    queryKey: ['customers', { q: search.q ?? null, page }],
    queryFn: () =>
      load(
        call(
          api.GET('/v1/staff/customers', {
            params: { query: { ...(search.q ? { q: search.q } : {}), page } },
          }),
        ),
      ),
    placeholderData: keepPreviousData,
  })
  const problem = problemOf(list.error)
  const data = list.data
  const fetching = list.isPlaceholderData
  const countId = useId()

  const paged = data ? data.total > data.pageSize || page > 1 : false
  const first = data ? (data.page - 1) * data.pageSize + 1 : 0
  return (
    <>
      <PageHeader title={m.customers.title}>
        <div className="flex flex-col gap-4 pt-2">
          <SearchForm
            label={m.customers.title}
            filtered={search.q !== undefined}
            onSubmit={() =>
              void navigate({ to: '/customers', search: { q: form.q.trim() || undefined } })
            }
            onClear={() => void navigate({ to: '/customers', search: {} })}
          >
            <SearchField
              label={m.customers.search}
              hint={m.customers.searchHint}
              value={form.q}
              onChange={(q) => setForm({ q })}
            />
          </SearchForm>
          <ResultCount
            id={countId}
            count={data ? plural(locale, m.customers.found, data.total) : null}
            range={
              data && paged && data.items.length > 0
                ? format(m.lists.range, { first, last: first + data.items.length - 1 })
                : null
            }
            asked={JSON.stringify(asked)}
            fetching={fetching}
          />
        </div>
      </PageHeader>

      <LoadState data={data} problem={problem}>
        {(data) =>
          data.total === 0 ? (
            search.q === undefined ? (
              <EmptyState icon={Users} title={m.customers.noneYet}>
                {m.customers.fills}
              </EmptyState>
            ) : (
              <EmptyState icon={SearchX} title={m.customers.none}>
                {m.lists.change}
              </EmptyState>
            )
          ) : (
            <ResultList
              countId={countId}
              fetching={fetching}
              pages={
                paged
                  ? {
                      page,
                      shown: data.page,
                      rows: data.items.length,
                      pageSize: data.pageSize,
                      total: data.total,
                      // The first page is the list's own address, with no page in it.
                      onPage: (next) =>
                        void navigate({
                          to: '/customers',
                          search: { ...search, page: next > 1 ? next : undefined },
                        }),
                    }
                  : undefined
              }
            >
              {data.items.length === 0 ? (
                <li className="py-3 text-text-muted">{m.lists.pastEnd}</li>
              ) : (
                data.items.map((customer) => (
                  <ListRow
                    key={customer.id}
                    title={
                      <Link
                        to="/customers/$customerId"
                        params={{ customerId: customer.id }}
                        className={cn('font-bold', ROW_LINK)}
                      >
                        {customer.lastName} {customer.firstName}
                      </Link>
                    }
                    end={
                      // S8: a record whose owner asked to be erased takes no new booking.
                      customer.erasureRequestedOn ? (
                        <StatusChip tone="warning">{m.customer.erasureRequested}</StatusChip>
                      ) : undefined
                    }
                  >
                    {customer.phone ? <span>{customer.phone}</span> : null}
                    {customer.email ? (
                      <span className="wrap-anywhere">{customer.email}</span>
                    ) : null}
                    <span>
                      {plural(locale, m.customers.bookings, customer.bookings)}
                      {customer.lastBooking
                        ? ` · ${format(m.customers.last, {
                            code: customer.lastBooking.code,
                            date: formatDateTime(locale, customer.lastBooking.startsAt),
                          })}`
                        : ''}
                    </span>
                  </ListRow>
                ))
              )}
            </ResultList>
          )
        }
      </LoadState>
    </>
  )
}
